import { createGraphQLHandler, mirageGraphQLFieldResolver } from '@miragejs/graphql';
import federatedSchema from 'garaje/mirage/graphql/federatedSchema';
import { capitalize } from '@ember/string';
import currentEnv from 'garaje/config/environment';
import _merge from 'lodash/merge';

function mockGraphQL(server, resolvers, config) {
  const { route, schema } = config;

  server.urlPrefix = currentEnv.apiHost;

  server.post(
    route,
    createGraphQLHandler(schema, server.schema, {
      root: {
        prefix: 'graphql',
      },
      resolvers,
    })
  );
}

export const defaultMocks = {
  RegistrationDate: {
    screeningCard(obj, args, context, info) {
      info.fieldName = 'screeningCardable';
      info.path.key = 'screeningCardable';
      const screeningCard = mirageGraphQLFieldResolver(obj, args, context, info);

      // we need to strip our mirage namespace and coerce to the graphql type
      screeningCard.__typename = capitalize(screeningCard.__typename.split('Graphql/')[1]);
      return screeningCard;
    },
  },
  RegistrationDatePartialDay: {
    screeningCard(obj, args, context, info) {
      info.fieldName = 'screeningCardable';
      info.path.key = 'screeningCardable';
      const screeningCard = mirageGraphQLFieldResolver(obj, args, context, info);

      // we need to strip our mirage namespace and coerce to the graphql type
      screeningCard.__typename = capitalize(screeningCard.__typename.split('Graphql/')[1]);
      return screeningCard;
    },
  },
  MapFloor: {
    features(obj, _args, _context, _info) {
      return obj.features.map((mapFeature) => {
        if (mapFeature.desk) {
          mapFeature.__typename = 'DeskMapFeature';
        } else if (mapFeature.room) {
          mapFeature.__typename = 'RoomMapFeature';
        } else {
          mapFeature.__typename = 'MapFeature';
        }
        return mapFeature;
      });
    },
  },
  AreaMap: {
    floors(obj, _args, _context, _info) {
      return obj.floors;
    },
  },
  RoomStatus: {
    status: () => 'AVAILABLE',
    time: () => new Date(),
  },
};

export const defaultRoombaMocks = {
  Query: {
    rooms(obj, args, context) {
      const { mirageSchema } = context;

      return mirageSchema['roomba/rooms'].all().models;
    },
    room(obj, args, context) {
      const { mirageSchema } = context;

      return mirageSchema['roomba/rooms'].find(args.id);
    },
    paginatedRooms(obj, args, context) {
      const { mirageSchema } = context;

      return {
        items: mirageSchema['roomba/rooms'].all().models,
        totalCount: mirageSchema['roomba/rooms'].all().models.length,
      };
    },
    company(obj, args, context) {
      const { mirageSchema } = context;

      return mirageSchema['roomba/companies'].first();
    },
    location(obj, args, context) {
      const { mirageSchema } = context;

      return mirageSchema['roomba/locations'].first();
    },
    spaceSaverConfig() {
      // TODO: implement tests for space saver configs
      return null;
    },
  },
  Mutation: {
    releaseRoom() {
      return null;
    },
    createCompany(obj, args, context) {
      const { mirageSchema } = context;

      return mirageSchema['roomba/companies'].first();
    },
    linkRoomsWithLocation(obj, args, context) {
      const { mirageSchema } = context;
      const rooms = mirageSchema['roomba/rooms'].find(args.roomIds);
      rooms.update({ location: mirageSchema['roomba/locations'].find(args.locationId) });
      return rooms.models;
    },
    unlinkConnection(obj, args, context) {
      const { mirageSchema } = context;
      // todo: remove a connection?
      return mirageSchema['roomba/companies'].first();
    },
    unpairDevice(obj, args, context) {
      const { mirageSchema } = context;
      const { id } = args;

      mirageSchema.db['roomba/rooms'].update('1', { deviceIds: [] });
      mirageSchema.db['roomba/devices'].remove(id);

      return 'great success';
    },
    updateCompanyConfig(obj, args, context) {
      const company = context.mirageSchema['roomba/companies'].first();
      return company.update(args.input);
    },
    updateRoomConfig(obj, args, context) {
      const room = context.mirageSchema['roomba/rooms'].find(args.input.id);
      return room.update(args.input);
    },
    updateLightedCaseType(obj, args, context) {
      const { mirageSchema } = context;
      const { deviceId, caseType } = args;

      const device = mirageSchema['roomba/devices'].findBy({
        id: deviceId,
      });
      device.ledAudioType = caseType;
      return device.save();
    },
    updateRoomsLocationShouldEmailGlobalAdmins(obj, args, context) {
      const { mirageSchema } = context;
      const { locationId, shouldEmailGlobalAdmins } = args;

      const location = mirageSchema['roomba/locations'].findBy({
        id: locationId,
      });
      location.roomsShouldEmailGlobalAdmins = shouldEmailGlobalAdmins;
      return location.save();
    },
    updateRoomsLocationShouldEmailLocationAdmins(obj, args, context) {
      const { mirageSchema } = context;
      const { locationId, shouldEmailLocationAdmins } = args;

      const location = mirageSchema['roomba/locations'].findBy({
        id: locationId,
      });
      location.roomsShouldEmailLocationAdmins = shouldEmailLocationAdmins;
      return location.save();
    },
    updateRoomsLocationNotificationEmails(obj, args, context) {
      const { mirageSchema } = context;
      const { locationId, notificationEmails } = args;

      const location = mirageSchema['roomba/locations'].findBy({
        id: locationId,
      });
      location.roomsNotificationEmails = notificationEmails;
      return location.save();
    },
  },
  RoomsFilterInput: {
    roomId: () => '1',
  },
  RoomStatus: {
    status: () => {
      // generate random statuses to alter UI
      const randStatus = Math.floor(Math.random() * 10) % 2;
      return randStatus ? 'AVAILABLE' : 'OCCUPIED';
    },
    time: () => new Date(),
  },
};

export function mockFederatedGraphql(server, mockObject) {
  return mockGraphQL(server, _merge(defaultMocks, defaultRoombaMocks, mockObject), {
    route: '/a/graphql_federated',
    schema: federatedSchema,
  });
}
